import React, { useEffect, useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import LaunchIcon from "@mui/icons-material/Launch";
import { Stack } from "@mui/material";
import { formatISO } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";

import useGetServiceTeams from "@fartherfinance/frontend/api/Entity/hooks/useGetServiceTeams";
import useCreateTaskV3 from "@fartherfinance/frontend/api/Tasks/hooks/useCreateTask.v3";
import type { PostTaskRequestV3 } from "@fartherfinance/frontend/api/Tasks/requests/postTask.v3";
import {
  ManualTaskTypeV2,
  TaskDetailsV3,
  TaskPriority,
  TaskStatus,
  TaskTypeV2,
} from "@fartherfinance/frontend/api/Tasks/Types";
import {
  mapTaskPriorityToLabel,
  mapTaskStatusToLabel,
  mapTaskTypeToLabelV2,
} from "@fartherfinance/frontend/api/Tasks/utils/mappings";
import {
  AdvisorId,
  ClientId,
  Custodian,
  ServiceTeamId,
} from "@fartherfinance/frontend/api/Types";

import Tasks from "../../Tasks";
import {
  assigneeDefaultOption,
  ATTACHMENTS_LIMIT_COUNT,
  TaskAssigneeAutocompleteOption,
} from "../common";
import { useServiceTeamsOptions, useTasksNavigation } from "../hooks";
import TaskAttachment from "../TaskDetails/Components/TaskAttachments/TaskAttachment";
import TaskMessageModal from "../TaskMessageModal/TaskMessageModal";
import {
  mapAssigneeToOption,
  mapSerivceTeamToOption,
  resolveServiceTeam,
} from "../utils";
import Drawer from "@src/multiCustodian/components/Drawer/Drawer";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import TaskFormV3 from "@src/multiCustodian/components/TaskForm/TaskFormV3";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useFilesManagement from "@src/multiCustodian/hooks/useFilesManagement";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import {
  EnhancedAutocompleteOption,
  TASK_CREATION_PROCESS_DOC,
} from "@src/multiCustodian/pages/Advisor/common";
import { mapPersonToOption } from "@src/multiCustodian/pages/Advisor/utils";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import AlertInline from "@src/sharedComponents/AlertInline/AlertInline";
import IconButton from "@src/sharedComponents/IconButton/IconButton";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import { MenuOption } from "@src/sharedComponents/Menu/MenuOption";
import Text from "@src/sharedComponents/Typography/Text";
import Upload from "@src/sharedComponents/Upload/Upload";
import { extractAxiosErrorMessage } from "@src/utils/axios";

import styles from "./CreateTask.module.css";

const mapTaskDetailsToFormData = (
  {
    title,
    taskPriority,
    taskType,
    serviceTeamId,
    assignee,
    client,
    advisor,
    dueDate,
    custodian,
  }: TaskDetailsV3,
  serviceTeamOptions: EnhancedAutocompleteOption<ServiceTeamId>[]
): Partial<FormData> => {
  return {
    parentTaskTitle: title,
    taskType:
      taskType !== TaskTypeV2.enum.LIQUIDATION
        ? { label: mapTaskTypeToLabelV2(taskType), value: taskType }
        : undefined,
    serviceTeam:
      serviceTeamOptions.find(
        (serviceTeamOption) => serviceTeamOption.value === serviceTeamId
      ) ?? null,
    advisor: advisor
      ? (mapPersonToOption(advisor) as EnhancedAutocompleteOption<AdvisorId>)
      : undefined,
    assignee: assignee ? mapAssigneeToOption(assignee) : null,
    client: client
      ? (mapPersonToOption(client) as EnhancedAutocompleteOption<ClientId>)
      : null,
    dueDate,
    custodian: custodian && { label: custodian, value: custodian },
    // NOTE: subtasks are always defaulting to not started status
    taskStatus: {
      label: mapTaskStatusToLabel(TaskStatus.enum.NOT_STARTED),
      value: TaskStatus.enum.NOT_STARTED,
    },
    taskPriority: {
      label: mapTaskPriorityToLabel(taskPriority),
      value: taskPriority,
    },
  };
};

const transformFormDataToPayload = (
  formData: FormData,
  enableTaskServiceTeams: boolean
): PostTaskRequestV3["payload"] => {
  const { assignee, serviceTeam } = formData;

  return {
    title: formData.title,
    description: formData.description,
    taskType: formData.taskType.value,
    // NOTE: for CX, if selected, we send the service team id, for advisors we always send undefined
    serviceTeamId: enableTaskServiceTeams ? serviceTeam?.value : undefined,
    advisorId: formData.advisor.value,
    assigneeId:
      assignee && assignee.value
        ? {
            type: assignee.meta.type,
            id: assignee.value,
          }
        : undefined,
    clientId: formData.client?.value,
    dueDate: formData.dueDate
      ? formatISO(formData.dueDate, { representation: "date" })
      : undefined,
    custodian: formData.custodian?.value,
    taskPriority: formData.taskPriority.value,
    taskStatus: formData.taskStatus.value,
  };
};

interface FormData {
  title: string;
  description: string;
  parentTaskTitle?: string;
  taskType: EnhancedAutocompleteOption<ManualTaskTypeV2>;
  serviceTeam: EnhancedAutocompleteOption<ServiceTeamId> | null;
  advisor: EnhancedAutocompleteOption<AdvisorId>;
  assignee: TaskAssigneeAutocompleteOption | null;
  client: EnhancedAutocompleteOption<ClientId> | null;
  dueDate: Date | null;
  custodian: EnhancedAutocompleteOption<Custodian> | null;
  taskStatus: EnhancedAutocompleteOption<TaskStatus>;
  taskPriority: EnhancedAutocompleteOption<TaskPriority>;
}

interface CreateTaskProps {
  isSubtaskCreation?: boolean;
}

const CreateTask: React.FC<CreateTaskProps> = ({ isSubtaskCreation }) => {
  const { enableTaskServiceTeams } = useFlags();

  const [isInitialRender, setIsInitialRender] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [
    isDoubleConfirmationModalVisible,
    setIsDoubleConfirmationModalVisible,
  ] = useState(false);
  const [isMutating, setIsMutating] = useState(false);

  const location = useLocation<TaskDetailsV3 | undefined>();
  const { files, setFiles, removeFile } = useFilesManagement({
    countLimit: ATTACHMENTS_LIMIT_COUNT,
  });
  const { handleNavigateToTasksList, handleNavigateToTaskDetails } =
    useTasksNavigation();

  const statusNotification = useStatusNotification();
  const auth = useAdvisorRequestAuth();
  const callCreateTaskV3 = useCreateTaskV3(auth);

  const isServiceTeamFieldVisibleForCX = Boolean(enableTaskServiceTeams);
  const serviceTeams = useGetServiceTeams(auth);
  const { serviceTeamsOptions } = useServiceTeamsOptions();
  const parentTaskDetails = location.state;

  const form = useForm<FormData>({
    mode: "onChange",
    defaultValues:
      isSubtaskCreation && parentTaskDetails
        ? mapTaskDetailsToFormData(parentTaskDetails, serviceTeamsOptions)
        : {
            taskStatus: {
              label: mapTaskStatusToLabel(TaskStatus.enum.NOT_STARTED),
              value: TaskStatus.enum.NOT_STARTED,
            },
            taskPriority: {
              label: mapTaskPriorityToLabel(TaskPriority.enum.MEDIUM),
              value: TaskPriority.enum.MEDIUM,
            },
            assignee: assigneeDefaultOption,
          },
  });

  //NOTE: formState is wrapped in Proxy so we need to access it as such in order to subscribe to changes
  const { isDirty } = form.formState;

  const formTaskType = form.watch("taskType");

  const resolvedServiceTeam = resolveServiceTeam(
    serviceTeams.data ?? [],
    formTaskType?.value,
    isServiceTeamFieldVisibleForCX
  );
  const isMissingServiceTeamAlertVisible =
    !serviceTeams.isLoading &&
    !isServiceTeamFieldVisibleForCX &&
    !resolvedServiceTeam;

  const isAttachmentsSectionVisible = files.length > 0;

  const parentDescription = parentTaskDetails?.description;

  const handleCloseDrawer = (): void => {
    if (isDirty) {
      setIsDoubleConfirmationModalVisible(true);
    } else {
      setIsDrawerOpen(false);
    }
  };

  const handleFormSubmit = (): void => {
    form.handleSubmit(async (formData) => {
      try {
        setIsMutating(true);

        await callCreateTaskV3({
          request: {
            payload: {
              ...transformFormDataToPayload(formData, enableTaskServiceTeams),
              parentId: parentTaskDetails?.id,
            },
            ...(files.length && { attachments: files }),
          },
          queryParams: {
            useDefaultServiceTeam: !enableTaskServiceTeams,
          },
        });
        setIsMutating(false);

        trackEvent({
          name: "Advisor Tasks Create",
          properties: {
            type: formData.taskType.value,
            priority: formData.taskPriority.value,
            status: formData.taskStatus.value,
          },
        });

        if (isSubtaskCreation && parentTaskDetails) {
          handleNavigateToTaskDetails(parentTaskDetails.id);
          statusNotification("Subtask created.", "Success");
        } else {
          handleNavigateToTasksList();
          statusNotification("Task created.", "Success");
        }
      } catch (error) {
        setIsMutating(false);
        statusNotification(
          extractAxiosErrorMessage(error, "Failed to create task."),
          "Error"
        );
      }
    })();
  };

  const handlePersistFiles = (files: FileList | null): void => {
    if (files && files.length) {
      setFiles(files);
    }
  };

  const handleCreateMenuOptions = (fileName: string): MenuOption[] => {
    return [
      {
        label: "Delete",
        className: styles.textWarning,
        onClick: () => removeFile(fileName),
      },
    ];
  };

  const handleOpenDocument = (): void => {
    window.open(TASK_CREATION_PROCESS_DOC, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    if (resolvedServiceTeam) {
      form.setValue("serviceTeam", mapSerivceTeamToOption(resolvedServiceTeam));
    }
  }, [form, resolvedServiceTeam]);

  return (
    <Tasks>
      <FormProvider {...form}>
        <Drawer
          isDrawerOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
          SlideProps={{
            onExited: handleNavigateToTasksList,
            onEntered: () => {
              setIsInitialRender(false);
            },
            timeout: isInitialRender && isSubtaskCreation ? 0 : undefined,
          }}
          {...(isInitialRender && isSubtaskCreation
            ? {
                ModalProps: {
                  componentsProps: { backdrop: { transitionDuration: 0 } },
                },
              }
            : {})}
          header={
            isSubtaskCreation &&
            parentTaskDetails && (
              <IconButton
                onClick={() =>
                  handleNavigateToTaskDetails(parentTaskDetails.id, {
                    from: "CREATE_SUBTASK",
                  })
                }
                IconComponent={ArrowBackIcon}
                iconClassName={styles.backIcon}
              />
            )
          }
          footer={
            <Stack
              sx={{ width: "100%" }}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Upload
                multiple
                onChange={handlePersistFiles}
                renderChild={(onClick) => (
                  <IconButton
                    iconClassName={styles.attachmentIcon}
                    onClick={onClick}
                    IconComponent={AttachFileIcon}
                  />
                )}
              />

              <div className={styles.actions}>
                <Button
                  variant="outlined"
                  buttonType="primary"
                  text="Cancel"
                  onClick={handleCloseDrawer}
                />
                <Button
                  variant="contained"
                  buttonType="primary"
                  text="Save"
                  disabled={isMutating || isMissingServiceTeamAlertVisible}
                  onClick={handleFormSubmit}
                />
              </div>
            </Stack>
          }
        >
          {isMissingServiceTeamAlertVisible && (
            <AlertInline
              className={styles.alert}
              severity="error"
              message="Cannot create task because advisor not assigned to service team. Contact Client Services to resolve."
            />
          )}

          <div className={styles.header}>
            <Text variant="title2">
              Create {isSubtaskCreation ? "Subtask" : "Task"}
            </Text>
            <Button
              variant="outlined"
              buttonType="secondary"
              text="Task Templates"
              endIcon={<LaunchIcon />}
              onClick={handleOpenDocument}
            />
          </div>

          <TaskFormV3
            isServiceTeamFieldVisible={isServiceTeamFieldVisibleForCX}
            isSubtaskCreation={isSubtaskCreation}
            parentDescription={parentDescription ?? undefined}
            parentTaskTitle={parentTaskDetails?.title ?? undefined}
          />

          {isAttachmentsSectionVisible && (
            <div className={styles.attachmentsContainer}>
              <div className={styles.attachmentsHeader}>
                <Text variant="title3">Attachments</Text>
                <Text variant="body" color="textSubtle">
                  {files.length}/{ATTACHMENTS_LIMIT_COUNT}
                </Text>
              </div>

              <div className={styles.attachments}>
                {files.map((file) => (
                  <TaskAttachment
                    key={file.name}
                    previewUrl={URL.createObjectURL(file)}
                    filename={file.name}
                    menuOptions={handleCreateMenuOptions(file.name)}
                  />
                ))}
              </div>
            </div>
          )}

          {isMutating && (
            <div className={styles.loading}>
              <LogoLoadingStill />
            </div>
          )}

          <TaskMessageModal
            title="Are you sure you want to leave without saving?"
            isOpen={isDoubleConfirmationModalVisible}
            onClose={{
              label: "Stay",
              action: () => setIsDoubleConfirmationModalVisible(false),
            }}
            onConfirm={{ label: "Leave", action: () => setIsDrawerOpen(false) }}
          >
            <Text variant="body" color="textSubtle">
              Your changes will not be saved.
            </Text>
          </TaskMessageModal>
        </Drawer>
      </FormProvider>
    </Tasks>
  );
};

export default CreateTask;
